import { type CSSProperties, type ReactElement, type ReactNode, useEffect, useRef } from "react";

import { Group, Table, UnstyledButton } from "@mantine/core";
import { Spacing } from "@snapchat/snap-design-system";
import { IconArrowsSort, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

import {
    bodyCell,
    bodyRow,
    clickableRow,
    flexCell,
    headerCell,
    headerRow,
    scrollContainer,
    sortButton,
    stickyHeader,
    table,
    tableSubtitle,
    tableTitle,
    tbody,
    wrapper,
} from "./VirtualTable.module.css";

const ROW_HEIGHT_ESTIMATE = Spacing.XL;
const LOAD_MORE_THRESHOLD_RATIO = 0.75;
const SORT_ICON_SIZE = 14;
const SORT_ASC = "asc";
const SORT_DESC = "desc";

export interface VirtualTableProps<TData> {
    data: TData[];
    columns: ColumnDef<TData>[];
    getRowId?: (row: TData) => string;
    globalFilter?: string;
    onGlobalFilterChange?: (updater: string | ((prev: string) => string)) => void;
    sorting?: SortingState;
    onSortingChange?: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
    flexColumns?: string[];
    onRowClick?: (row: TData) => (() => void) | undefined;
    hasMore?: boolean;
    onLoadMore?: () => void;
    title?: ReactNode;
    subtitle?: ReactNode;
    header?: ReactNode;
    emptyMessage?: ReactNode;
    maxHeight?: CSSProperties["maxHeight"];
}

const colSizeVar = (size: number): CSSProperties => ({ "--vt-col-size": `${size}px` }) as CSSProperties;

const rowOffsetVar = (offset: number): CSSProperties => ({ "--vt-row-offset": `${offset}px` }) as CSSProperties;

export const VirtualTable = <TData,>({
    data,
    columns,
    getRowId,
    globalFilter,
    onGlobalFilterChange,
    sorting,
    onSortingChange,
    title,
    subtitle,
    flexColumns,
    onRowClick,
    hasMore,
    onLoadMore,
    header,
    emptyMessage,
    maxHeight,
}: VirtualTableProps<TData>): ReactElement => {
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const reactTable = useReactTable({
        data,
        columns,
        getRowId,
        state: {
            globalFilter,
            sorting,
        },
        onGlobalFilterChange,
        onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const { rows } = reactTable.getRowModel();

    const virtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => ROW_HEIGHT_ESTIMATE,
        overscan: 10,
    });

    const virtualItems = virtualizer.getVirtualItems();
    const lastItemIndex = virtualItems.at(-1)?.index;

    useEffect(() => {
        if (lastItemIndex === undefined || !hasMore || !onLoadMore) return;
        if (lastItemIndex >= Math.floor(rows.length * LOAD_MORE_THRESHOLD_RATIO)) {
            onLoadMore();
        }
    }, [lastItemIndex, rows.length, hasMore, onLoadMore]);

    return (
        <div className={wrapper}>
            {title && <div className={`heading-h400 ${tableTitle}`}>{title}</div>}
            {subtitle && <div className={`subheading-sh400 ${tableSubtitle}`}>{subtitle}</div>}
            {header}
            <div
                ref={tableContainerRef}
                className={scrollContainer}
                style={
                    {
                        "--vt-max-height": typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
                    } as CSSProperties
                }
                data-empty={rows.length === 0 || undefined}
            >
                <Table className={table} horizontalSpacing="sm" verticalSpacing={5} withRowBorders>
                    <Table.Thead className={stickyHeader}>
                        {reactTable.getHeaderGroups().map((headerGroup) => (
                            <Table.Tr key={headerGroup.id} className={headerRow}>
                                {headerGroup.headers.map((h) => {
                                    const content = (
                                        <Group gap={4} wrap="nowrap" align="center">
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                            {h.column.getCanSort() && !h.column.getIsSorted() && (
                                                <IconArrowsSort size={SORT_ICON_SIZE} />
                                            )}
                                            {h.column.getIsSorted() === SORT_ASC && (
                                                <IconChevronUp size={SORT_ICON_SIZE} />
                                            )}
                                            {h.column.getIsSorted() === SORT_DESC && (
                                                <IconChevronDown size={SORT_ICON_SIZE} />
                                            )}
                                        </Group>
                                    );
                                    return (
                                        <Table.Th
                                            key={h.id}
                                            className={`${headerCell} ${flexColumns?.includes(h.id) ? flexCell : ""}`}
                                            style={colSizeVar(h.getSize())}
                                            aria-sort={
                                                h.column.getIsSorted() === SORT_ASC
                                                    ? "ascending"
                                                    : h.column.getIsSorted() === SORT_DESC
                                                      ? "descending"
                                                      : h.column.getCanSort()
                                                        ? "none"
                                                        : undefined
                                            }
                                        >
                                            {h.column.getCanSort() ? (
                                                <UnstyledButton
                                                    className={sortButton}
                                                    onClick={h.column.getToggleSortingHandler()}
                                                >
                                                    {content}
                                                </UnstyledButton>
                                            ) : (
                                                content
                                            )}
                                        </Table.Th>
                                    );
                                })}
                            </Table.Tr>
                        ))}
                    </Table.Thead>
                    <Table.Tbody
                        className={tbody}
                        style={{ "--vt-total-size": `${virtualizer.getTotalSize()}px` } as CSSProperties}
                    >
                        {virtualItems.map((virtualRow) => {
                            const row = rows[virtualRow.index];
                            if (!row) return null;
                            return (
                                <Table.Tr
                                    key={row.id}
                                    data-index={virtualRow.index}
                                    className={`${bodyRow} ${onRowClick?.(row.original) ? clickableRow : ""}`}
                                    ref={(node) => {
                                        virtualizer.measureElement(node);
                                    }}
                                    style={rowOffsetVar(virtualRow.start)}
                                    onClick={onRowClick?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <Table.Td
                                            key={cell.id}
                                            className={`${bodyCell} ${flexColumns?.includes(cell.column.id) ? flexCell : ""}`}
                                            style={colSizeVar(cell.column.getSize())}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Table.Td>
                                    ))}
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </div>
            {rows.length === 0 && emptyMessage}
        </div>
    );
};
